import { useEffect, useMemo, useState } from 'react';
import { CommerceNoticeDoc } from '@gooduncles/gu-app-schema';
import { map } from 'rxjs/operators';

import { observeCommerceNotice, observeCommerceNotices } from 'src/utils/firebase/firebase-short-cut';
import { WHERE } from 'src/utils/firebase/firebase-manager';

/**
 * 공지사항 목록을 구독합니다.
 */
export const useCommerceNotices = (wheres?: WHERE[]) => {
  const [notices, setNotices] = useState<CommerceNoticeDoc[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const stableWheres = useMemo(() => JSON.stringify(wheres), [wheres]);

  useEffect(() => {
    const parsedWheres: WHERE[] = stableWheres ? JSON.parse(stableWheres) : [];
    setIsLoading(true);
    const subscription = observeCommerceNotices(parsedWheres)
      .pipe(
        map((docs) =>
          docs
            .filter((doc) => !doc._timeDelete)
            .sort((a, b) => {
              if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
              return (b._timeCreate?.toMillis?.() ?? 0) - (a._timeCreate?.toMillis?.() ?? 0);
            }),
        ),
      )
      .subscribe({
        next: (docs) => {
          setNotices(docs);
          setIsLoading(false);
        },
      });

    return () => subscription.unsubscribe();
  }, [stableWheres]);

  return { notices, isLoading };
};

/**
 * 단일 공지사항을 구독합니다.
 */
export const useCommerceNoticeById = (noticeId: string | undefined) => {
  const [notice, setNotice] = useState<CommerceNoticeDoc | undefined>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (noticeId) {
      setIsLoading(true);
      const subscription = observeCommerceNotice(noticeId).subscribe((doc) => {
        setNotice(doc);
        setIsLoading(false);
      });

      return () => {
        subscription.unsubscribe();
        setNotice(undefined);
        setIsLoading(false);
      };
    } else {
      setNotice(undefined);
      setIsLoading(false);
    }
  }, [noticeId]);

  return { notice, isLoading };
};
